//REV-21368 — types of the single-document page (Pure View).
//
//The shared vocabulary lives in `lib/types/closing-documents.types.ts` (promoted there by this
//ticket). Only what is specific to THIS surface is declared below.
//
//REV-21363 moved `DocumentPreviewKind` to `components/document-preview/document-preview.types.ts`,
//next to the preview component that was extracted out of here. The re-export below keeps every
//existing import path working — same move, same reason, same shape as REV-21368's own promotion of
//the shared vocabulary out of `documents-hub.types.ts`.

import {
    AssignmentOrigin,
    AssignmentState,
    AssignmentTarget,
    DocumentAccountRef,
    DocumentFiling,
    DocumentReadingState,
    DocumentUpstreamState,
    DocumentValidity,
} from '../../types/closing-documents.types';
import { DocumentPreviewKind } from '../document-preview/document-preview.types';

export { DocumentPreviewKind } from '../document-preview/document-preview.types';

/**
 * REV-21869 #164 (AP8c) — what the AI read from the document, as the panel needs it.
 *
 * `disabled` — REV-22010 gave it its producer: the backend answers it when the organisation switched
 * the normalisation off (`reason: 'notEnabled'`) or its token budget is spent (`reason:
 * 'limitReached'`).
 */
export type DocumentInterpretationState = 'value' | 'interpreting' | 'noValue' | 'none' | 'disabled';

/** What a value is: the balance, or — for a Depotauszug — the market value and the cost. */
export type DocumentValueLabel = 'BALANCE' | 'MARKET_VALUE' | 'COST';

/**
 * One value the AI read. `amountMinor` is the WIRE value, untouched — the panel's `data-amount-minor`
 * carries the raw storage form. `amount` is the SAME number divided by 100 exactly once, in the FE
 * mapper (`closing-document.selectors.ts`): the component only formats an already-converted number.
 */
export interface DocumentInterpretationValue {
    label: DocumentValueLabel;
    amountMinor: number;
    amount: number;
    currency: string;
    /** ISO date the value refers to ("Saldo per 31.12.2025"). */
    date?: string;
    /** The document line the value was quoted from. */
    quote?: string;
}

/**
 * REV-22010 — WHY there is no value, from a CLOSED list the backend maps its refusal codes onto
 * (`document-pipeline-feedback.ts`). Only ever on `none` or `disabled`. The view turns it into text
 * with a TOTAL key map (`presenters/document-feedback-keys.ts`) — never the raw value.
 */
export type DocumentInterpretationReason =
    | 'notEnabled'
    | 'limitReached'
    | 'closingHasNoBalanceSheetDate'
    | 'readingUnusable'
    | 'modelAnswerUnusable'
    | 'kindNotSupported'
    | 'noBookingOwner'
    | 'statementDatesUnreadable'
    | 'statementEndsBeforeBalanceSheetDate'
    | 'quoteNotInDocument'
    | 'amountUnreadable';

/** REV-22010 — why the classifier proposed no account (closed list, mapped by the backend). */
export type DocumentProposalRefusal = 'noAccountForKind' | 'accountsNotClassified' | 'severalAccountsMatch';

/**
 * REV-22010 — the QUALITY of a read that happened: the structure verdict of the stored extraction
 * head. Nothing else — never a page, a cell or a figure.
 */
export interface DocumentReadingQuality {
    structure: 'PASS' | 'FAIL';
}

export interface DocumentInterpretationView {
    state: DocumentInterpretationState;
    /** `value` only: one entry per value read, in the order the AI returned them. */
    values?: readonly DocumentInterpretationValue[];
    /** The document's own date (ISO), information only. */
    documentDate?: string;
    /** REV-22010 — only on `none`/`disabled`, and only when the backend knows one. */
    reason?: DocumentInterpretationReason;
}

/**
 * Everything this page shows about one document. The three sidebar states are DERIVED from
 * `assignment` / `filing` exactly as ADR-0009 §9 derives them upstream — there is no
 * `sidebarState` field, because a third truth beside the other two would drift out of step.
 */
export interface DocumentDetail {
    id: string;
    filename: string;

    previewKind: DocumentPreviewKind;
    /**
     * Handed to the browser's own PDF viewer (ADR-0009 §13). The component NEVER fetches it, and
     * the pipeline only serves it at `scanStatus === CLEAN`. `null` → the missing-preview
     * placeholder, which is a DIFFERENT state from `previewKind === 'other'`.
     */
    previewUrl: string | null;

    filing: DocumentFiling;
    assignment: AssignmentState;
    /**
     * The booking owner when `assignment === 'assigned'`, the PROPOSED account when `'proposed'`.
     * `null` when open, and for every `projectFile` document — a Projekt-Akte document is filed
     * and has no account (§9a).
     */
    account: DocumentAccountRef | null;
    /**
     * Only read when assigned. Absent/`null` is LEGACY DATA (#57) — a document assigned before
     * this field existed — and is deliberately NOT `'manual'`: nobody knows who filed it.
     */
    assignedBy?: AssignmentOrigin | null;
    /**
     * 0–100, or `null` → renders `—`. Absent is the NORMAL state until REV-21358 lands
     * (ADR-0009 §8) and must never read as an error.
     */
    confidence: number | null;

    /** The OPPOSITE of `projectFile`, not a variant of it (§9a). Reversible from the header. */
    notRelevant: boolean;
    validity: DocumentValidity;
    /**
     * REV-21869 #164 — absent for a document the backend never scoped an interpretation to (every
     * pre-existing fixture, and never for a live `proposal`, W2). Present, the panel renders next to
     * `Gültigkeit`; the component never invents a state the mapper did not send.
     */
    interpretation?: DocumentInterpretationView;

    /**
     * REV-21964 · C1-C4 / U2 - where the upstream file stands. Absent means `present`, which is also
     * every manual upload: SharePoint has no opinion about a file it never held.
     *
     * WARNUNG: A LABEL, never a removal. The document keeps its account and its reconciliation even
     * when SharePoint no longer has the file (C1); this page's job is to SAY so. ADR-0009 §4 has no
     * delete path, and `notRelevant` in the header stays the only retreat - reversible, and the
     * trustee's to choose.
     */
    upstreamState?: DocumentUpstreamState;
    /**
     * When that state last CHANGED, not when it was last checked. ISO-8601, formatted by the view.
     *
     * The distinction matters to a trustee reading it months later: "noticed on the 3rd" is a fact
     * about the folder, "checked on the 3rd" would be a fact about us.
     */
    upstreamStateChangedAt?: string | null;
    /**
     * `true` when the notice is the LOUD kind (C2) - the document backs an account.
     *
     * Decided by the container, like `upstreamNoticeIsUrgent` on the hub's row and for the same
     * reason: a view that re-derives "is this assigned?" from what it happens to render is a second
     * opinion about one fact, and two opinions drift.
     */
    upstreamNoticeIsUrgent?: boolean;
    /** ID2 - pre-composed list of the other upstream names for these bytes, or absent. */
    aliasHint?: string;

    /** REV-22010 — has the AI read the file? Absent = read (then `readingQuality` may say how well). */
    readingState?: DocumentReadingState;
    /** REV-22010 — the verdict of a finished read. Absent = not read. */
    readingQuality?: DocumentReadingQuality;
    /**
     * REV-22010 — why no account was proposed. Rendered only in the picker state of an UNFILED document:
     * a filed one has its answer, and a live proposal is its own panel.
     */
    proposalRefusal?: DocumentProposalRefusal;
}

/**
 * Payload of `assignmentChange`. Carries NO validity, unlike the hub's `AccountAssignEvent`.
 *
 * The hub's picker has to ask for validity, because the hub has nowhere else to ask. This surface
 * does: the `Gültigkeit` panel stands in the sidebar in all three states. Asking a second time
 * inside the picker would put the same question in two places on one screen, so the picker is ONE
 * step — pick an account or the Projekt-Akte, and that is the whole gesture.
 */
export interface DocumentAssignmentChangeEvent {
    documentId: string;
    target: AssignmentTarget;
}

/** Payload of `validityChange` — the standing `Gültigkeit` panel, live in all three states. */
export interface DocumentValidityChangeEvent {
    documentId: string;
    validity: DocumentValidity;
}

/**
 * Which sidebar the page shows. DERIVED, never stored (ADR-0009 §9):
 *   filed (account or Projekt-Akte) → `assigned`
 *   else a live proposal            → `proposal`
 *   else                            → `picker`
 * `Ändern` and `Ablehnen` open the picker over any of them; that is transient widget state.
 */
export type DocumentSidebarState = 'proposal' | 'assigned' | 'picker';

/**
 * A write the user started on this page and whose answer is not back yet. The part it
 * belongs to is greyed out and takes no clicks; `choice` is what the user picked, shown under the grey
 * where that is simple (an account id or `projectFile`, a validity, `accept`/`reject`).
 *
 * Controlled: the host sets it when it dispatches and clears it when the write is done.
 */
export interface DocumentDetailBusy {
    part: 'relevance' | 'proposal' | 'assignment' | 'validity';
    choice?: string;
}
